class CountryCurrencySelector extends window.BAO.CustomElement() {
  // localStorage key holding the customer's chosen country code.
  // Its presence means an explicit selection has been made.
  countryKey = 'BAO-country-refy'
  // sessionStorage key: set when the geo modal is dismissed so it does not
  // reappear on every page for the rest of the session.
  geoDismissedKey = 'BAO-geo-dismissed-refy'
  geoSelectorKey = 'geo-selector'

  static get requiredElements () {
    return ['option']
  }

  connectedCallback () {
    super.connectedCallback()

    // Only the geolocation-modal instance owns the shadow list and the
    // page-load location flow (it is always present on every page).
    if (this.isGeo && this.els['geo-current']?.exists) {
      this.shadow = this.els['shadow-store'].element.attachShadow({ mode: 'open' })
      this.shadowContent = this.querySelector('[data-shadow]').content

      this.runLocationFlow()
    }
  }

  setupListeners () {
    super.setupListeners()

    // setupListeners runs before connectedCallback, so read datasets here.
    const { currencyCode, currentHost, isGeolocationModal } = this.dataset
    this.currency = this.targetCurrency = currencyCode
    this.currentHost = currentHost
    this.isGeo = isGeolocationModal === 'true'

    this.handleOptionClick = this.handleOptionClick.bind(this)
    this.handleGeoButtonClick = this.handleGeoButtonClick.bind(this)
    this.handleGeoSelectChange = this.handleGeoSelectChange.bind(this)
    this.close = this.close.bind(this)

    // Direct option clicks only apply to the drawer list, not the <select>.
    if (!this.isGeo) {
      this.els.option.elements.forEach(option => {
        this.listeners.add(option, 'click', this.handleOptionClick)
      })
    }

    if (this.externalEls['geo-close']?.exists) {
      this.listeners.add(this.externalEls['geo-close'].element, 'click', this.close)
    }

    if (this.els['geo-select']?.exists) {
      this.listeners.add(this.els['geo-select'].element, 'change', this.handleGeoSelectChange)
    }

    if (this.els['geo-button']?.exists) {
      this.listeners.add(this.els['geo-button'].element, 'click', this.handleGeoButtonClick)
    }
  }

  /**
   * Runs once on load to decide whether the geolocation modal should open.
   *
   * Priority:
   * 1. ?country= handoff from another store -> store choice, strip param, silent.
   * 2. ?currency= switch on the current store -> store matching country, silent.
   * 3. Stored choice on the matching host -> silent (the "chosen url").
   * 4. Stored choice on a different host -> prompt (landed on wrong store).
   * 5. Nothing stored -> geolocate; prompt only if the detected country
   *    belongs to a different store than the one being viewed.
   */
  async runLocationFlow () {
    try {
      if (this.handleCountryParam()) return
      if (this.handleCurrencyParam()) return

      // Never redirect or prompt inside the theme editor or on preview domains.
      if (window.Shopify?.designMode || this.isPreviewHost) return

      const stored = this.storedCountry

      if (stored && this.countries[stored]) {
        if (this.countries[stored].host === this.currentHost) return

        // Stored preference points at a different store: user landed on the
        // wrong one. Prompt (unless already dismissed this session).
        if (!this.isGeoDismissed) this.openGeoModal(stored)
        return
      }

      const detected = await this.detectCountry()
      const resolved = detected ? this.resolveCountry(detected) : null
      if (!resolved) return

      if (resolved.host === this.currentHost) {
        // Already on the correct store; remember it silently.
        this.setStoredCountry(resolved.code)
        return
      }

      if (!this.isGeoDismissed) this.openGeoModal(resolved.code)
    } catch (error) {
      console.error('Error in runLocationFlow:', error)
    }
  }

  /** @returns {boolean} true when a ?country= param was handled. */
  handleCountryParam () {
    const param = this.getParam('country')
    if (!param) return false

    const code = param.toUpperCase()
    if (this.countries[code]) {
      this.setStoredCountry(code)
    }

    this.stripParam('country')
    return true
  }

  /** @returns {boolean} true when a ?currency= param was handled. */
  handleCurrencyParam () {
    const param = this.getParam('currency')
    if (!param) return false

    const match = this.findCountryByCurrencyOnHost(param.toUpperCase())
    if (match) {
      this.setStoredCountry(match.code)
    }

    return true
  }

  handleOptionClick (event) {
    const target = event.target.closest('[data-country-currency-selector-el="option"]')
    if (!target) return

    this.setTargetFrom(target)
    this.switchStore()
  }

  handleGeoSelectChange (event) {
    this.setGeoCurrent(event.target.value)
  }

  handleGeoButtonClick () {
    const select = this.els['geo-select'].element
    const selectedOption = select.options[select.selectedIndex]

    this.setTargetFrom(selectedOption)
    this.switchStore()
  }

  setTargetFrom (element) {
    this.targetHost = element.dataset.host
    this.targetCurrency = element.dataset.currencyCode
    this.targetCountryCode = element.dataset.countryCode
  }

  /**
   * Applies the current target selection:
   * - same host + same currency -> just close the drawer
   * - same host + different currency -> switch currency via ?currency=
   * - different host -> redirect to the target store, carrying ?country=
   */
  switchStore () {
    if (this.targetHost === this.currentHost && this.targetCurrency === this.currency) {
      this.close()
      return
    }

    if (this.targetHost === this.currentHost) {
      this.setStoredCountry(this.targetCountryCode)
      this.updateSearchParams({ currency: this.targetCurrency })
      return
    }

    this.setStoredCountry(this.targetCountryCode)
    this.goToStore()
  }

  goToStore () {
    const currentPath = window.location.pathname
    const searchParams = new URLSearchParams(window.location.search)

    // The destination market sets its own currency from the country, so drop
    // any stale currency param to avoid a country/currency mismatch.
    searchParams.delete('currency')

    const countryParam = this.targetCountryParam
    if (countryParam) {
      searchParams.set('country', countryParam)
    }

    const query = searchParams.toString()
    window.location.href = `https://${this.targetHost}${currentPath}${query ? `?${query}` : ''}`
  }

  get targetCountryParam () {
    const entry = this.countries[this.targetCountryCode]
    if (entry && Array.isArray(entry.covers) && entry.covers.length > 0) {
      return entry.covers[0]
    }
    return this.targetCountryCode
  }

  updateSearchParams (params) {
    const searchParams = new URLSearchParams(window.location.search)
    Object.keys(params).forEach(key => searchParams.set(key, params[key]))
    window.location.search = searchParams.toString()
  }

  openGeoModal (code) {
    this.setGeoCurrent(code)
    window.drawers.activeDrawerKey = this.geoSelectorKey
  }

  setGeoCurrent (code) {
    if (!this.shadowContent) return

    const optionHtml = this.shadowContent.querySelector(`[data-country-code="${code}"]`)
    if (!optionHtml) return

    this.els['geo-current'].element.innerHTML = optionHtml.innerHTML
    this.els['geo-select'].element.value = code
  }

  close () {
    window.drawers.activeDrawerKey = ''

    // Dismissing the geo modal without choosing suppresses it for the session.
    if (this.isGeo) {
      window.sessionStorage.setItem(this.geoDismissedKey, 'true')
    }
  }

  async detectCountry () {
    try {
      const res = await fetch('/browsing_context_suggestions.json').then(response => response.json())
      return res?.detected_values?.country?.handle || null
    } catch (error) {
      console.error('Error detecting country:', error)
      return null
    }
  }

  /**
   * Maps a detected ISO country code to a configured country entry.
   * Prefers an exact match, then falls back to any entry whose "covers" list
   * (configured in Theme settings) includes the code, e.g. the Europe entry.
   *
   * @param {string} code
   * @returns {object|null}
   */
  resolveCountry (code) {
    const upper = code.toUpperCase()

    if (this.countries[upper]) return this.countries[upper]

    const region = Object.values(this.countries).find(
      country => Array.isArray(country.covers) && country.covers.includes(upper),
    )

    return region || null
  }

  findCountryByCurrencyOnHost (currency) {
    return Object.values(this.countries).find(
      country =>
        country.host === this.currentHost &&
        (country.currency || '').toUpperCase() === currency,
    ) || null
  }

  getParam (name) {
    return new URLSearchParams(window.location.search).get(name)
  }

  stripParam (name) {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(name)) return

    url.searchParams.delete(name)
    window.history.replaceState({}, '', url.toString())
  }

  setStoredCountry (code) {
    if (!code) return
    window.localStorage.setItem(this.countryKey, code.toUpperCase())
  }

  get storedCountry () {
    const value = window.localStorage.getItem(this.countryKey)
    return value ? value.toUpperCase() : null
  }

  get isGeoDismissed () {
    return window.sessionStorage.getItem(this.geoDismissedKey) === 'true'
  }

  get isPreviewHost () {
    return /\.myshopify\.com$/.test(window.location.hostname)
  }

  get countries () {
    return (window.theme && window.theme.countries) || {}
  }
}

if (!customElements.get('country-currency-selector')) {
  customElements.define('country-currency-selector', CountryCurrencySelector)
}
